import { useQuery } from "react-query";
import { loggedInApiCall } from "src/api/axiosClient/userApi";
import { z } from "zod";
import { zodParseWithLog } from "src/api/apiErrorLogs";
import { SsqPropertiesSchema, SsqProperty } from "src/api/sure-api/actions/useGetOneAction";
import { Post, PostSchema } from "../../post/postSchema";
import { OlapScoresQuery } from "src/context/OlapScores/useOlapScoresParams";
import { isQueryReady } from "src/api/axiosClient/utils";
import { FieldSchema, ConverterSchema } from "src/api/sure-api/ssq/useGetOneSsqVersion";
import { DataListOptionSchema } from "src/api/sure-api/dataList/useGetAllDataList";
import { ACTION_IMAGE_REFRESH_QUERY_OPTIONS } from "src/api/actionImageRefresh";

export const getOneActionPlanActionsKey = "getOneActionPlanActions" as const;

type Options = {
    actionPlanActionId: string | undefined;
    actionPlanID: string | undefined;
    scoresQuery?: OlapScoresQuery;
};

export type TargetSsqProperty = {
    id: number;
    title: string;
    name?: string;
    parent?: {
        id: number;
        name?: string;
        title: string;
    };
    unit?: {
        abbreviation?: string;
    };
    children?: {
        id: number;
    }[];
};

export type ActionFormula = {
    id: number;
    formula: string | null;
    targetSsqProperty?: TargetSsqProperty;
};

export type ActionFieldUnit = {
    abbreviation: string;
};

export type ActionField = z.infer<typeof ActionFieldSchema>;

const TargetSsqPropertySchema = z.object({
    id: z.number(),
    title: z.string(),
    name: z.string().optional(),
    unit: z
        .object({
            abbreviation: z.string().optional(),
        })
        .optional(),
    parent: z
        .object({
            id: z.number(),
            title: z.string(),
            name: z.string().optional(),
        })
        .optional(),
    children: z
        .object({
            id: z.number(),
        })
        .array()
        .optional(),
});

const ActionFormulaSchema: z.ZodType<ActionFormula> = z.object({
    id: z.number(),
    formula: z.string().nullable(),
    targetSsqProperty: z.preprocess((val) => val ?? undefined, TargetSsqPropertySchema.optional()),
});

const ActionFieldUnitSchema = z.object({
    id: z.number().optional(),
    abbreviation: z.string(),
    code: z.string().optional(),
    quantityKind: z.string().nullable().optional(),
    factorToBase: z.number().nullable().optional(),
    converterEligible: z.boolean().optional(),
});

export type DataListOption = z.infer<typeof DataListOptionSchema>;

const DataListSchema = z.object({
    options: DataListOptionSchema.array(),
});

const ActionFieldSchema = z.object({
    id: z.number(),
    question: z.string(),
    contributionTypes: z.string().array(),
    hidden: z.boolean(),
    actionFormulas: ActionFormulaSchema.array().optional(),
    unit: ActionFieldUnitSchema.optional(),
    converter: ConverterSchema.optional().nullable(),
    isMultivalue: z.boolean(),
    field: FieldSchema.optional(),
    dataList: DataListSchema.optional(),
});

export const useGetOneActionPlanAction = (options: Options) => {
    const { actionPlanActionId, actionPlanID, scoresQuery } = options;

    const isEnabled = actionPlanActionId !== undefined && actionPlanID !== undefined;
    const query = useQuery({
        queryKey: [getOneActionPlanActionsKey, actionPlanActionId, actionPlanID, scoresQuery],
        staleTime: 5 * 60 * 1000,
        enabled: isEnabled,
        ...ACTION_IMAGE_REFRESH_QUERY_OPTIONS,
        queryFn: async () => {
            const url = `/api/action_plans/${actionPlanID}/action/${actionPlanActionId}`;

            const response = await loggedInApiCall.get(url, {
                params: {
                    ...scoresQuery,
                },
            });
            return response;
        },
        select: (resp) => {
            // Normalize undefined fields to empty arrays before validation
            const normalizedData = {
                ...resp.data,
                impactData: resp.data.impactData ?? [],
                totalStart: resp.data.totalStart ?? [],
                totalEnd: resp.data.totalEnd ?? [],
                totalImpact: resp.data.totalImpact ?? [],
            };

            const data = zodParseWithLog(normalizedData, ActionPlanActionSchema, getOneActionPlanActionsKey);
            return data;
        },
    });

    const isReady = isQueryReady(query, isEnabled);

    return {
        isReady,
        ...query,
    };
};

type PostWithoutScores = Pick<Post, "id" | "campaign" | "elements">;
const PostWithoutScoresSchema = PostSchema.pick({
    id: true,
    campaign: true,
    elements: true,
});

type PropertiesTreeElement = {
    ssq_property_id: number;
    position: number;
    value: string;
};

const PropertiesTreeElementSchema = z.object({
    ssq_property_id: z.number(),
    position: z.number(),
    value: z.string(),
});

export type PropertiesTree = Record<string, number[]>;

export type ScoreId = string;
export type PropertyId = string;
export type PropertyPosition = string;

export type ScorePerScoreId = Record<ScoreId, number | null>;

export type ActionImpactData = {
    actionId: number;
    title: string;
    startElement: string;
    endElement: string;
    elementChange: string | null;
    fieldType?: string;
    position: number;
    propertyUnit: string;
    impactScores: Record<ScoreId, number>;
};
export type ImpactDataByPosition = Partial<Record<PropertyPosition, ActionImpactData[]>>;
export type ImpactData = Partial<Record<PropertyId, ImpactDataByPosition>>;

const ActionImpactDataSchema = z.object({
    actionId: z.number(),
    title: z.string(),
    startElement: z.string(),
    endElement: z.string(),
    elementChange: z.string().nullable(),
    fieldType: z.string().optional(),
    position: z.number(),
    propertyUnit: z.string(),
    impactScores: z.record(z.string(), z.number()),
});

// Accept both arrays and objects (for when arrays have gaps and become objects)
const ImpactDataByPositionSchema: z.ZodType<ImpactDataByPosition> = z.record(
    z.string(),
    ActionImpactDataSchema.array(),
);
const ImpactDataSchema: z.ZodType<ImpactData> = z.record(z.string(), ImpactDataByPositionSchema);

export const ScorePerScoreIdSchema = z.record(z.string(), z.number().or(z.null()));

export type ActionCsrTheme = {
    id: number;
    title: string;
    parent?: {
        id: number;
        title: string;
    };
};

const ActionCsrThemeSchema = z.object({
    id: z.number(),
    title: z.string(),
    parent: z
        .object({
            id: z.number(),
            title: z.string(),
        })
        .optional(),
});

export const ActionPlanActionSchema: z.ZodType<ActionPlanAction> = z.object({
    id: z.string(),
    actionPlan: z.string(),
    actionId: z.number(),
    action: z.object({
        id: z.number(),
        title: z.string(),
        description: z.string().optional(),
        imageUrl: z.string().url().optional(),
        ssqProperties: SsqPropertiesSchema.array(),
        propertiesTree: z.record(z.string(), z.number().array()).optional(),
        actionFields: ActionFieldSchema.array().optional(),
        csrThemes: ActionCsrThemeSchema.array(),
    }),
    startSimulationSsqPost: PostWithoutScoresSchema,
    endSimulationSsqPost: PostWithoutScoresSchema,
    type: z.string().optional(),
    userMessageTitle: z.string().optional(),
    userMessageBody: z.string().optional(),
    assignee: z.string().optional(),
    impactData: ImpactDataSchema,
    propertiesTreeElements: PropertiesTreeElementSchema.array(),

    totalStart: ScorePerScoreIdSchema.or(z.tuple([])),
    totalEnd: ScorePerScoreIdSchema.or(z.tuple([])),
    totalImpact: ScorePerScoreIdSchema.or(z.tuple([])),
    totalImpactPercentage: ScorePerScoreIdSchema,
});

export type ActionPlanAction = {
    id: string;
    actionPlan: ActionPlan;
    actionId: number;
    action: {
        id: number;
        title: string;
        description?: string;
        imageUrl?: string;
        ssqProperties: SsqProperty[];
        propertiesTree?: PropertiesTree;
        actionFields?: ActionField[];
        csrThemes: ActionCsrTheme[];
    };
    startSimulationSsqPost: PostWithoutScores;
    endSimulationSsqPost: PostWithoutScores;
    type?: string;
    userMessageTitle?: string;
    userMessageBody?: string;
    assignee?: string; // IRI string from API
    impactData: ImpactData;
    propertiesTreeElements: PropertiesTreeElement[];

    totalStart: ScorePerScoreId | [];
    totalEnd: ScorePerScoreId | [];
    totalImpact: ScorePerScoreId | [];
    totalImpactPercentage: ScorePerScoreId;
};

type ActionPlan = IRI;
